import { createFileRoute } from "@tanstack/react-router";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useContext, useEffect, useMemo } from "react";
import { ConfigContext, OrderDataContext, ReturnOrderContext } from "@/Layout";
import { useCalculateReturn } from "@/api/calculateReturn";
import "@/api/verify";
import ImageWithFallback from "@/components/ImageWithFallback/ImageWithFallback";
import NextButton from "@/components/NextButton/NextButton";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrency, getNextRoute } from "@/utils/utils";

export const Route = createFileRoute("/return-item/$id/exchange")({
  component: ExchangePage,
});

function ExchangePage() {
  const { returnOrder, setReturnOrder } = useContext(ReturnOrderContext);
  const { orderData } = useContext(OrderDataContext);
  const returnItemId = useParams({ from: "/return-item/$id/exchange" }).id;
  const configData = useContext(ConfigContext);
  const navigate = useNavigate();
  const { data: calculatedReturn, refetch: refetchCalculatedReturn } =
    useCalculateReturn();

  useEffect(() => {
    // If this is the last item to exchange, calculate the prepayment
    if (
      returnOrder &&
      returnOrder.line_items.some((item) => item.exchange_variant_id) &&
      orderData?.exchange_data?.prepaid &&
      returnOrder.line_items.findIndex((item) => item.id === returnItemId) ===
        returnOrder.line_items.length - 1
    ) {
      refetchCalculatedReturn();
    }
  }, [returnOrder, refetchCalculatedReturn, orderData, returnItemId]);

  const lineItem = useMemo(
    () => orderData?.line_items.find((item) => item.id === returnItemId),
    [returnItemId, orderData]
  );

  const validExchangeOptions = useMemo(() => {
    return Object.entries(orderData?.exchange_data?.variant_data ?? {})
      .filter(([variantId]) => {
        return orderData?.exchange_data?.options[
          lineItem?.variant_id?.toString() ?? ""
        ].includes(Number(variantId));
      })
      .map(([, variant]) => variant);
  }, [orderData, lineItem]);

  useEffect(() => {
    if (!configData || !orderData || !returnOrder)
      navigate({
        to: "/",
        search: (prev) => prev,
      });
  }, [configData, orderData, returnOrder, navigate]);

  const availableStock = useMemo(
    () =>
      new Map(
        Object.entries(orderData?.exchange_data?.variant_data ?? {}).map(
          ([variantId, variant]) => {
            const initialStock = variant.inventory_quantity;
            const selectedStock = returnOrder?.line_items
              .filter((item) => item.exchange_variant_id === Number(variantId))
              .reduce((acc) => acc + 1, 0);
            return [variantId, initialStock - (selectedStock ?? 0)];
          }
        )
      ),
    [orderData, returnOrder]
  );

  const selectedVariant = useMemo(() => {
    if (!returnOrder?.line_items.find((item) => item.id === returnItemId))
      return null;
    const variantId = returnOrder?.line_items.find(
      (item) => item.id === returnItemId
    )?.exchange_variant_id;

    if (!variantId) return null;

    return orderData?.exchange_data?.variant_data[variantId.toString()];
  }, [returnOrder, returnItemId, orderData]);

  const setExchangeVariant = (variantId: number | undefined): void => {
    if (!returnOrder) return;
    setReturnOrder({
      ...returnOrder,
      line_items: returnOrder.line_items.map((item) => {
        if (item.id === returnItemId)
          return { ...item, exchange_variant_id: variantId };
        return item;
      }),
    });
  };

  if (!configData || !orderData || !returnOrder) return null;
  if (!lineItem || !lineItem.product_id || !lineItem.variant_id) return null;

  return (
    <ReturnContainer>
      <div className="sm:min-w-[600px]">
        <h1 className="font-semibold text-lg">{configData.exchange_header}</h1>
        <p className="text-sm min-h-12 mt-2">{configData.exchange_message}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:justify-between mt-8 gap-4 sm:gap-16">
          <div>
            <h2 className="text-base font-semibold">
              {configData.return_item_text}
            </h2>
            <hr className="my-2" />
            <div className="flex">
              <ImageWithFallback src={lineItem?.product_src} />
              <div className="flex flex-col ml-2">
                <span className="text-xs font-semibold">
                  {lineItem?.product_title}
                </span>
                <span className="text-xs font-light">
                  {lineItem?.variant_title}
                </span>
              </div>
            </div>
          </div>
          <div className="order-3 md:order-none mt-4 sm:mt-0">
            <h2 className="text-base font-semibold">
              {configData.exchange_to_text}
            </h2>
            <hr className="my-2" />
            <div className="flex">
              {selectedVariant ? (
                <>
                  <ImageWithFallback
                    src={selectedVariant.src ?? lineItem.product_src}
                  />
                  <div className="flex flex-col ml-2">
                    <span className="text-xs font-medium">
                      {lineItem?.product_title}
                    </span>
                    <span className="text-xs font-light">
                      {selectedVariant.title}
                    </span>
                  </div>
                </>
              ) : (
                <i className="text-sm opacity-50">
                  {configData.no_exchange_selected_text}
                </i>
              )}
            </div>
          </div>
          <div className="grid order-2 md:order-none col-span-2">
            <div className="flex flex-col w-full text-xs font-light">
              <Select
                onValueChange={(value) => {
                  setExchangeVariant(Number.parseInt(value));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      configData.exchange_select_dropdown_text ??
                      "Select exchange"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="??">
                      <span>
                        {configData.exchange_select_none_dropdown_text ??
                          "No exchange"}
                      </span>
                    </SelectItem>
                    {validExchangeOptions.map((option) => {
                      return (
                        <SelectItem
                          value={option.id.toString()}
                          disabled={
                            (availableStock.get(option.id.toString()) ?? 0) <= 0
                          }
                        >
                          <div className="flex gap-2">
                            <img
                              className="size-4"
                              src={option.src ?? lineItem.product_src}
                            ></img>
                            <span className="text-xs">{option.title}</span>
                            <span className="font-light"> • </span>
                            <span className="text-xs">
                              {formatCurrency(
                                option.price.amount,
                                option.price.currency_code
                              )}
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="mt-auto py-8">
          <NextButton
            onClick={() => {
              navigate({
                to: getNextRoute(
                  returnItemId,
                  orderData,
                  returnOrder,
                  "exchange",
                  calculatedReturn
                ),
                search: (prev) => prev,
              });
            }}
            text={
              returnOrder?.line_items.find((item) => item.id === returnItemId)
                ?.exchange_variant_id
                ? configData.exchange_button_exchange_message
                : configData.exchange_button_no_exchange_message
            }
            buttonColor={configData.button_color}
          />
        </div>
      </div>
    </ReturnContainer>
  );
}
